import {
  buildChildrensBookPhysicalPageItems as manuscriptUnitNavigationItems,
  childrensBookWriterPageLabel as manuscriptUnitWriterPageLabel,
} from "../utils/childrensBookPhysicalPagination";

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import ChildrensBookManuscriptImportPreview from "./ChildrensBookManuscriptImportPreview";
import ChildrensBookManuscriptUnitDivisionReview from "./ChildrensBookManuscriptUnitDivisionReview";

import ChildrensBookManuscriptIllustrationContext from "./ChildrensBookManuscriptIllustrationContext";
import ChildrensBookManuscriptFullPageSpreadLayout, {
  manuscriptFullPageSpreadLayoutApplies,
} from "./ChildrensBookManuscriptFullPageSpreadLayout";
import ChildrensBookManuscriptFullPageSingleLayout, {
  manuscriptFullPageSingleLayoutApplies,
} from "./ChildrensBookManuscriptFullPageSingleLayout";
import ChildrensBookManuscriptSharedIllustrationPageLayout, {
  manuscriptSharedIllustrationPageLayoutApplies,
  manuscriptSharedIllustrationPageProfile,
} from "./ChildrensBookManuscriptSharedIllustrationPageLayout";
import ChildrensBookManuscriptEditor from "./ChildrensBookManuscriptEditor";
import ChildrensBookChapterSceneNavigatorV1 from "./ChildrensBookChapterSceneNavigatorV1";
import ChildrensBookChapterSceneManagementV1B from "./ChildrensBookChapterSceneManagementV1B";
import { childrensBookWriterPresentationV1 } from "../utils/childrensBookWriterPresentationV1";

import {
  MANUSCRIPT_PAGE_MAXIMUM_WORDS,
} from "../utils/childrensBookManuscriptUnitDivision";

import {
  consultChildrensBookManuscriptFlowFromUnit,
} from "../utils/childrensBookManuscriptFlowProjection";

import {
  countWordsFromManuscriptEntry,
  manuscriptContentFromPlainText,
} from "../utils/childrensBookManuscriptContent";

import {
  manuscriptEntryFieldsFromEditorContent,
} from "../utils/childrensBookManuscriptEditor";

import {
  prepareChildrensBookSharedManuscriptEdit,
} from "../utils/childrensBookSharedManuscriptEdit";

import {
  unitRequiresText,
} from "../utils/childrensBookUnitDiagnostics";

// This is a rendering boundary, not a minimum book size.
// Books with 1–20 units show every unit. Books with more
// than 20 units are displayed in bounded ranges. Zero units
// continues to use the existing empty Book Structure state.
const MANUSCRIPT_RANGE_SIZE = 20;

function cleanText(value) {
  return String(value ?? "").trim();
}



export function manuscriptLiveFlowAdvisory(
  consultation,
  isFR = false
) {
  if (
    !consultation?.ok ||
    !consultation?.consultable ||
    !consultation?.projection?.ok
  ) {
    return null;
  }

  const projection =
    consultation.projection;

  const additionalPagesRequired =
    Math.max(
      0,
      Number(
        projection
          .additionalPagesRequired
      ) || 0
    );

  if (
    additionalPagesRequired > 0
  ) {
    if (
      consultation
        .windowTruncated
    ) {
      return {
        kind:
          "continues_beyond_window",

        text: isFR
          ? "Le texte continue au-delà des pages voisines actuellement vérifiées."
          : "Text continues beyond the nearby pages currently being checked.",
      };
    }

    return {
      kind:
        "additional_pages_required",

      text: isFR
        ? `Cette section a besoin de ${additionalPagesRequired} page${additionalPagesRequired === 1 ? "" : "s"} de texte supplémentaire${additionalPagesRequired === 1 ? "" : "s"} pour le contenu actuel.`
        : `This section needs ${additionalPagesRequired} additional text page${additionalPagesRequired === 1 ? "" : "s"} for the current amount of text.`,
    };
  }

  const vacatedUnitIds =
    Array.isArray(
      projection
        .vacatedUnitIds
    )
      ? projection
          .vacatedUnitIds
      : [];

  if (
    vacatedUnitIds.length > 0
  ) {
    return {
      kind:
        "backward_pull",

      text: isFR
        ? `Votre texte pourrait tenir sur moins de pages. ${vacatedUnitIds.length} page${vacatedUnitIds.length === 1 ? "" : "s"} voisine${vacatedUnitIds.length === 1 ? "" : "s"} pourrai${vacatedUnitIds.length === 1 ? "t" : "ent"} devenir disponible${vacatedUnitIds.length === 1 ? "" : "s"}.`
        : `Your text could fit on fewer pages. ${vacatedUnitIds.length} nearby page${vacatedUnitIds.length === 1 ? "" : "s"} may become available.`,
    };
  }

  const projectedPages =
    Array.isArray(
      projection
        .projectedPages
    )
      ? projection
          .projectedPages
      : [];

  if (
    projectedPages.length > 1
  ) {
    return {
      kind:
        "forward_flow",

      text: isFR
        ? `Le texte peut se poursuivre naturellement sur ${projectedPages.length} pages voisines.`
        : `Text can continue naturally across ${projectedPages.length} nearby pages.`,
    };
  }

  return null;
}

function normalizedManuscriptRangeStart(
  value,
  totalUnits
) {
  const total =
    Math.max(
      0,
      Number(totalUnits) || 0
    );

  if (total < 1) {
    return 0;
  }

  const requested =
    Number(value);

  const aligned =
    Number.isInteger(requested) &&
    requested >= 0
      ? Math.floor(
          requested /
            MANUSCRIPT_RANGE_SIZE
        ) *
        MANUSCRIPT_RANGE_SIZE
      : 0;

  const maximum =
    Math.floor(
      (total - 1) /
        MANUSCRIPT_RANGE_SIZE
    ) *
    MANUSCRIPT_RANGE_SIZE;

  return Math.min(
    aligned,
    maximum
  );
}

function manuscriptUnitWritingDestinationLabel(
  item,
  isFR
) {
  if (!item) {
    return "";
  }

  const unit =
    item.unit || {};

  const isSpread =
    unit?.unitType ===
      "double_page_spread";

  const illustrationPlan =
    cleanText(
      unit?.illustrationPlan
    ).toLowerCase();

  const illustrationPageSide =
    cleanText(
      unit?.illustrationPageSide
    ).toLowerCase();

  /*
   * A full-page illustration occupies one complete
   * side of a two-page spread, so manuscript text
   * belongs on the opposite page.
   *
   * Smaller illustrations may share a text page or
   * participate in a broader spread composition, so
   * those retain the full spread destination here.
   */
  if (
    isSpread &&
    illustrationPlan ===
      "full_page" &&
    illustrationPageSide ===
      "left"
  ) {
    return `Page ${item.pageEnd}`;
  }

  if (
    isSpread &&
    illustrationPlan ===
      "full_page" &&
    illustrationPageSide ===
      "right"
  ) {
    return `Page ${item.pageStart}`;
  }

  return manuscriptUnitWriterPageLabel(
    item,
    isFR
  );
}

function manuscriptAdjacentNavigationLabel(
  item,
  direction,
  isFR
) {
  const previous =
    Number(direction) < 0;

  const spread =
    item?.unit?.unitType ===
      "double_page_spread";

  if (isFR) {
    if (spread) {
      return previous
        ? "Double page précédente"
        : "Double page suivante";
    }

    return previous
      ? "Page précédente"
      : "Page suivante";
  }

  if (spread) {
    return previous
      ? "Previous Spread"
      : "Next Spread";
  }

  return previous
    ? "Previous Page"
    : "Next Page";
}

function manuscriptUnitDestinationLabel(
  item,
  isFR
) {
  if (!item) {
    return "";
  }

  const pages =
    manuscriptUnitWriterPageLabel(
      item,
      isFR
    );

  if (
    item.pageEnd >
    item.pageStart
  ) {
    return isFR
      ? `Double page — ${pages}`
      : `Two-page Spread — ${pages}`;
  }

  return isFR
    ? `Page simple — ${pages}`
    : `Single Page — ${pages}`;
}

export default function ChildrensBookManuscriptPlan({
  lang = "en",
  readOnly = false,
  novelWritingStyle = false,
  chapterSceneNavigationV1 = false,
  chapterSceneManagementV1B = false,
  chapterSceneOutline = [],
  projectPlan = {},
  bookMap,
  manuscriptPlan,
  childrensBookAssets = [],
  onLoadChildrensBookAssetFile,
  initialUnitId = "",
  focusedUnitId = "", // Opt-in ONLY for the Children's Book Novel-style Studio
  initialRangeStart = 0,
  returnToWholeBookReview = false,
  onPreviewManuscriptImport,
  onApplyManuscriptImport,
  onApplyManuscriptUnitDivision,
  manuscriptUnitDivisionSaving = false,
  onChange,
  onOpenBookMap,
  onPositionChange,
  onReturn,
  onExit,
}) {
  const isFR = String(lang).toLowerCase() === "fr";
  const focusedUnit = cleanText(focusedUnitId);
  const units = useMemo(
    () => (Array.isArray(bookMap?.units) ? bookMap.units : []),
    [bookMap?.units]
  );
  const entries = useMemo(
    () => (Array.isArray(manuscriptPlan?.entries) ? manuscriptPlan.entries : []),
    [manuscriptPlan?.entries]
  );

  const divisionProjectPlan =
    useMemo(
      () => ({
        ...projectPlan,
        bookMap,
        manuscriptPlan,
      }),
      [
        projectPlan,
        bookMap,
        manuscriptPlan,
      ]
    );

  const unitNavigationItems =
    useMemo(
      () =>
        manuscriptUnitNavigationItems(
          units
        ),
      [
        units,
      ]
    );
  const [
    editingId,
    setEditingId,
  ] = useState(
    () => {
      const unitId =
        String(
          initialUnitId || ""
        ).trim();

      return unitId || null;
    }
  );

  const manuscriptSelectionHandoffRef =
    useRef(
      new Map()
    );

  const [
    manuscriptSelectionRestore,
    setManuscriptSelectionRestore,
  ] = useState(null);

  const manuscriptSelectionRestoreRevisionRef =
    useRef(0);

  const [pendingDeleteId, setPendingDeleteId] =
    useState(null);

  const [
    divisionUnitId,
    setDivisionUnitId,
  ] = useState(null);

  const [
    divisionOutcome,
    setDivisionOutcome,
  ] = useState(null);

  const [
    rangeStart,
    setRangeStart,
  ] = useState(
    () => {
      const requestedUnitId =
        cleanText(
          initialUnitId
        );

      const requestedIndex =
        unitNavigationItems.findIndex(
          (item) =>
            cleanText(
              item.unit?.id
            ) ===
            requestedUnitId
        );

      if (
        requestedIndex >= 0
      ) {
        return Math.floor(
          requestedIndex /
            MANUSCRIPT_RANGE_SIZE
        ) *
          MANUSCRIPT_RANGE_SIZE;
      }

      return normalizedManuscriptRangeStart(
        initialRangeStart,
        units.length
      );
    }
  );

  const boundedRangeStart =
    normalizedManuscriptRangeStart(
      rangeStart,
      units.length
    );

  const visibleUnits =
    useMemo(
      () => focusedUnit
        ? units.filter(unit => cleanText(unit?.id) === focusedUnit)
        : units.slice(boundedRangeStart, boundedRangeStart + MANUSCRIPT_RANGE_SIZE),
      [boundedRangeStart, units, focusedUnit]
    );

  const visibleNavigationItems =
    useMemo(
      () => focusedUnit
        ? unitNavigationItems.filter(item => cleanText(item.unit?.id) === focusedUnit)
        : unitNavigationItems.slice(boundedRangeStart, boundedRangeStart + MANUSCRIPT_RANGE_SIZE),
      [boundedRangeStart, unitNavigationItems, focusedUnit]
    );

  const liveFlowConsultation =
    useMemo(
      () => {
        if (!editingId) {
          return null;
        }

        const result =
          consultChildrensBookManuscriptFlowFromUnit({
            units,
            entries,

            activeUnitId:
              editingId,
          });

        return result?.ok
          ? result
          : null;
      },
      [
        editingId,
        entries,
        units,
      ]
    );

  const liveFlowAdvisory =
    useMemo(
      () =>
        manuscriptLiveFlowAdvisory(
          liveFlowConsultation,
          isFR
        ),
      [
        isFR,
        liveFlowConsultation,
      ]
    );

  const totalPhysicalPages =
    unitNavigationItems.length
      ? unitNavigationItems[
          unitNavigationItems.length - 1
        ].pageEnd
      : 0;

  const visiblePhysicalPageStart =
    visibleNavigationItems[0]
      ?.pageStart || 0;

  const visiblePhysicalPageEnd =
    visibleNavigationItems[
      visibleNavigationItems.length - 1
    ]?.pageEnd || 0;

  const rangeCount =
    Math.max(
      1,
      Math.ceil(
        units.length /
          MANUSCRIPT_RANGE_SIZE
      )
    );

  const initialUnitHandledRef =
    useRef("");

  const pendingScrollUnitIdRef =
    useRef("");

  useEffect(() => {
    if (
      rangeStart !==
      boundedRangeStart
    ) {
      setRangeStart(
        boundedRangeStart
      );
    }
  }, [
    boundedRangeStart,
    rangeStart,
  ]);

  useEffect(() => {
    const unitId =
      cleanText(
        initialUnitId
      );

    if (
      !unitId ||
      initialUnitHandledRef.current ===
        unitId
    ) {
      return;
    }

    const item =
      unitNavigationItems.find(
        (candidate) =>
          cleanText(
            candidate.unit?.id
          ) ===
          unitId
      );

    if (!item) {
      return;
    }

    const nextRangeStart =
      Math.floor(
        item.index /
          MANUSCRIPT_RANGE_SIZE
      ) *
        MANUSCRIPT_RANGE_SIZE;

    initialUnitHandledRef.current =
      unitId;

    pendingScrollUnitIdRef.current =
      unitId;

    setRangeStart(
      nextRangeStart
    );

    setEditingId(
      unitId
    );
  }, [
    initialUnitId,
    unitNavigationItems,
  ]);

  useEffect(() => {
    const unitId =
      cleanText(
        pendingScrollUnitIdRef.current
      );

    if (
      !unitId ||
      cleanText(
        editingId
      ) !== unitId ||
      !visibleUnits.some(
        (unit) =>
          cleanText(
            unit?.id
          ) === unitId
      )
    ) {
      return;
    }

    const target =
      typeof document !==
        "undefined"
        ? document.getElementById(
            `manuscript-unit-${encodeURIComponent(
              unitId
            )}`
          )
        : null;

    if (!target) {
      return;
    }

    pendingScrollUnitIdRef.current =
      "";

    target.scrollIntoView?.({
      behavior: "smooth",
      block: "start",
    });
  }, [
    boundedRangeStart,
    editingId,
    visibleUnits,
  ]);

  useEffect(() => {
    if (
      typeof onPositionChange !==
      "function"
    ) {
      return;
    }

    onPositionChange({
      unitId:
        cleanText(
          editingId
        ),

      rangeStart:
        boundedRangeStart,
    });
  }, [
    boundedRangeStart,
    editingId,
    onPositionChange,
  ]);

  const statusOptions = [
    ["not_started", isFR ? "Non commencé" : "Not started"],
    ["drafting", isFR ? "Rédaction en cours" : "Drafting"],
    ["ready_for_review", isFR ? "Prêt pour révision" : "Ready for review"],
    ["revised", isFR ? "Révisé" : "Revised"],
    ["complete", isFR ? "Terminé" : "Complete"],
  ];

  function writingStatusLabel(status) {
    return (
      statusOptions.find(
        ([value]) => value === status
      )?.[1] ||
      (
        isFR
          ? "État non indiqué"
          : "Status not provided"
      )
    );
  }

  function openManuscriptUnit(
    requestedUnitId
  ) {
    const unitId =
      cleanText(
        requestedUnitId
      );

    const item =
      unitNavigationItems.find(
        (candidate) =>
          cleanText(
            candidate.unit?.id
          ) === unitId
      );

    if (!item) {
      return;
    }

    const nextRangeStart =
      Math.floor(
        item.index /
          MANUSCRIPT_RANGE_SIZE
      ) *
        MANUSCRIPT_RANGE_SIZE;

    pendingScrollUnitIdRef.current =
      unitId;

    setPendingDeleteId(
      null
    );

    setDivisionUnitId(
      null
    );

    setRangeStart(
      nextRangeStart
    );

    setEditingId(
      unitId
    );
  }


  function moveToAdjacentManuscriptUnit(
    offset
  ) {
    if (focusedUnit) return;
    const currentIndex =
      unitNavigationItems.findIndex(
        (item) =>
          cleanText(
            item.unit?.id
          ) ===
          cleanText(
            editingId
          )
      );

    const nextItem =
      unitNavigationItems[
        currentIndex +
          Number(offset || 0)
      ];

    if (!nextItem) {
      return;
    }

    openManuscriptUnit(
      nextItem.unit?.id
    );
  }


  function entryFor(unitId) {
    return entries.find((entry) => entry.unitId === unitId) || {
      unitId,
      manuscriptText: "",
      authorNote: "",
      status: "not_started",
    };
  }

  function rememberManuscriptSelectionHandoff(
    unitId,
    requestedSelection
  ) {
    const resolvedUnitId =
      cleanText(
        unitId
      );

    const blockId =
      cleanText(
        requestedSelection
          ?.blockId
      );

    if (
      !resolvedUnitId ||
      !blockId
    ) {
      if (
        resolvedUnitId
      ) {
        manuscriptSelectionHandoffRef
          .current
          .delete(
            resolvedUnitId
          );
      }

      return null;
    }

    const start =
      Math.max(
        0,
        Number(
          requestedSelection
            ?.start
        ) || 0
      );

    const end =
      Math.max(
        0,
        Number(
          requestedSelection
            ?.end
        ) || 0
      );

    const token = {
      unitId:
        resolvedUnitId,

      selection: {
        blockId,
        start,
        end,
      },
    };

    manuscriptSelectionHandoffRef
      .current
      .set(
        resolvedUnitId,
        token
      );

    return token;
  }


  function handleManuscriptEditorChange(
    unitId,
    fields = {}
  ) {
    if (readOnly) return;
    const sourceSelectionToken =
      rememberManuscriptSelectionHandoff(
        unitId,
        fields
          ?.selectionHandoff
      );

    setManuscriptSelectionRestore(
      null
    );

    const sharedEdit =
      prepareChildrensBookSharedManuscriptEdit({
        units,
        entries,

        unitId,

        manuscriptContent:
          fields
            ?.manuscriptContent,

        selectionHandoff:
          sourceSelectionToken
            ?.selection,

        editIntent:
          fields
            ?.editIntent,
      });

    if (!sharedEdit?.ok) {
      return;
    }

    if (
      !sharedEdit
        .flowApplied
    ) {
      onChange(
        "entries",
        sharedEdit
          .nextEntries
      );

      return;
    }

    const targetUnitId =
      cleanText(
        sharedEdit
          .targetUnitId
      );

    const restorableSelection =
      sharedEdit
        .restorableSelection;

    const targetEntrySignature =
      String(
        sharedEdit
          .targetEntrySignature ||
        ""
      );

    if (
      !targetUnitId ||
      !restorableSelection
        ?.selection ||
      !targetEntrySignature
    ) {
      return;
    }

    manuscriptSelectionRestoreRevisionRef
      .current += 1;

    const selectionRestore = {
      unitId:
        targetUnitId,

      expectedEntrySignature:
        targetEntrySignature,

      revision:
        manuscriptSelectionRestoreRevisionRef
          .current,

      selection:
        restorableSelection
          .selection,
    };

    onChange(
      "entries",
      sharedEdit
        .nextEntries
    );

    if (
      targetUnitId !==
      cleanText(
        unitId
      )
    ) {
      openManuscriptUnit(
        targetUnitId
      );
    }

    setManuscriptSelectionRestore(
      selectionRestore
    );
  }

  useEffect(() => {
    if (
      !manuscriptSelectionRestore
    ) {
      return;
    }

    if (
      cleanText(
        manuscriptSelectionRestore
          ?.unitId
      ) ===
      cleanText(
        editingId
      )
    ) {
      return;
    }

    setManuscriptSelectionRestore(
      null
    );
  }, [
    editingId,
    manuscriptSelectionRestore,
  ]);


  function updateEntry(unitId, field, value) {
    if (readOnly) return;
    let changedFields;

    if (field === "manuscriptText") {
      changedFields = {
        manuscriptText: value,
        manuscriptContent:
          manuscriptContentFromPlainText(
            value
          ),
      };
    } else if (field === "manuscriptContent") {
      const fields =
        manuscriptEntryFieldsFromEditorContent(
          value
        );

      if (!fields.ok) {
        return;
      }

      changedFields = {
        manuscriptText:
          fields.manuscriptText,
        manuscriptContent:
          fields.manuscriptContent,
      };
    } else {
      changedFields = {
        [field]: value,
      };
    }

    const exists = entries.some((entry) => entry.unitId === unitId);
    const nextEntries = exists
      ? entries.map((entry) =>
          entry.unitId === unitId ? { ...entry, ...changedFields } : entry
        )
      : [...entries, {
          unitId,
          manuscriptText: "",
          authorNote: "",
          status: "not_started",
          ...changedFields,
        }];

    onChange("entries", nextEntries);
  }

  function closeDivisionReview(
    outcome = {}
  ) {
    if (outcome?.applied) {
      const summary =
        outcome.result
          ?.applicationSummary ||
        {};

      setDivisionOutcome({
        sourceUnitId:
          cleanText(
            summary.sourceUnitId ||
            divisionUnitId
          ),

        segmentCount:
          Number(
            summary.segmentCount ||
            0
          ),

        sourceContentMode:
          cleanText(
            summary
              .sourceContentMode
          ),
      });
    }

    setDivisionUnitId(
      null
    );
  }

  function requestDeleteEntry(unitId) {
    setPendingDeleteId(unitId);
  }

  function cancelDeleteEntry() {
    setPendingDeleteId(null);
  }

  function confirmDeleteEntry(event) {
    event.preventDefault();
    if (readOnly) return;

    if (!pendingDeleteId) return;

    onChange(
      "entries",
      entries.filter(
        (entry) =>
          entry.unitId !== pendingDeleteId
      )
    );

    setEditingId(null);
    setPendingDeleteId(null);
  }

  const totalWords = entries.reduce(
    (total, entry) =>
      total +
      countWordsFromManuscriptEntry(
        entry
      ),
    0
  );
  const started = entries.filter(
    (entry) =>
      countWordsFromManuscriptEntry(
        entry
      ) > 0
  ).length;
  const complete = entries.filter(
    (entry) => entry.status === "complete"
  ).length;

  function continueAfterImport(
    outcome = {}
  ) {
    const affectedIds =
      Array.isArray(
        outcome
          ?.summary
          ?.affectedUnitIds
      )
        ? outcome
            .summary
            .affectedUnitIds
            .map(
              (value) =>
                String(
                  value || ""
                ).trim()
            )
            .filter(Boolean)
        : [];

    const firstAffectedId =
      affectedIds.find(
        (unitId) =>
          units.some(
            (unit) =>
              String(
                unit?.id || ""
              ).trim() ===
              unitId
          )
      );

    if (firstAffectedId) {
      const affectedItem =
        unitNavigationItems.find(
          (item) =>
            cleanText(
              item.unit?.id
            ) ===
            firstAffectedId
        );

      if (affectedItem) {
        const nextRangeStart =
          Math.floor(
            affectedItem.index /
              MANUSCRIPT_RANGE_SIZE
          ) *
            MANUSCRIPT_RANGE_SIZE;

        pendingScrollUnitIdRef.current =
          firstAffectedId;

        setRangeStart(
          nextRangeStart
        );
      }

      setEditingId(
        firstAffectedId
      );
    }
  }


  const unitLabel = (unit) => {
    const labels = {
      single_page: isFR ? "Page simple" : "Single page",
      double_page_spread: isFR ? "Double page" : "Two-page spread",
      front_matter: isFR ? "Page liminaire" : "Front matter page",
      back_matter: isFR ? "Page finale" : "Back matter page",
      activity_page: isFR ? "Page d’activité" : "Activity page",
    };
    return labels[unit.unitType] || unit.unitType;
  };

  const illustrationLabel = (value) => ({
    none:
      isFR
        ? "Aucune illustration"
        : "No illustration",
    small_illustration:
      isFR
        ? "Petite illustration"
        : "Small illustration",
    half_page:
      isFR
        ? "Illustration sur une demi-page"
        : "Half-page illustration",
    full_page:
      isFR
        ? "Illustration pleine page"
        : "Full-page illustration",
    full_spread:
      isFR
        ? "Illustration sur toute la double page"
        : "Full-spread illustration",
    integrated_text:
      isFR
        ? "Texte intégré à l’illustration"
        : "Text integrated with illustration",
  }[value] || value);

  return (
    <section
      className="ns-nbb-workspace-page ns-nbb-manuscript-page"
      data-cb-v1e4f-focused-editor={focusedUnit ? "true" : "false"}
      aria-label={focusedUnit
        ? (isFR ? "Rédiger la page ou double page" : "Write Selected Page or Spread")
        : (isFR ? "Plan du manuscrit" : "Manuscript Plan")}
    >
      {focusedUnit ? <style>{`
        [data-cb-v1e4f-focused-editor="true"] .ns-nbb-manuscript-unit-nav { display: none !important; }
      `}</style> : null}
      <div style={{ display: focusedUnit ? "none" : "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap", marginBottom: 18 }}>
        <div>
          <div className="ns-nbb-eyebrow" style={{ fontSize: 12, fontWeight: 900, letterSpacing: "0.12em", textTransform: "uppercase", color: "#b45309" }}>
            NextScenes Book Blueprint · {isFR ? "Plan du manuscrit" : "Manuscript Plan"}
          </div>
          <h2 className="ns-section-title ns-nbb-page-title" style={{ margin: "5px 0 8px" }}>
            {isFR ? "Rédigez page par page" : "Write Page by Page"}
          </h2>
          <p className="ns-card-meta" style={{ margin: 0, maxWidth: 780, lineHeight: 1.6 }}>
            {isFR
              ? "Chaque unité reprend les indications du Plan du livre afin de guider la rédaction."
              : "Each page or spread follows the Book Structure to support organised writing."}
          </p>
        </div>
        <button className="ns-button ns-button-outline" type="button" onClick={onExit}>
          {isFR ? "Retour à Mes projets" : "Return to My Projects"}
        </button>
      </div>

      {chapterSceneManagementV1B ? (
        <ChildrensBookChapterSceneManagementV1B
          units={units}
          lang={lang}
          onOpenUnit={openManuscriptUnit}
        />
      ) : chapterSceneNavigationV1 ? (
        <ChildrensBookChapterSceneNavigatorV1
          units={units}
          chapters={chapterSceneOutline}
          lang={lang}
          onOpenUnit={openManuscriptUnit}
        />
      ) : null}

      {!readOnly && !focusedUnit ? <ChildrensBookManuscriptImportPreview
        lang={lang}
        bookMapUnits={
          units
        }
        manuscriptEntries={
          entries
        }
        manuscriptPlan={
          manuscriptPlan
        }
        onPreviewManuscriptImport={
          onPreviewManuscriptImport
        }
        onApplyManuscriptImport={
          onApplyManuscriptImport
        }
        onContinueAfterImport={
          continueAfterImport
        }
      /> : null}

      {novelWritingStyle ? <style>{`
        [data-novel-writer-v1="true"] [data-manuscript-editor-surface="true"] {
          min-height: 380px !important; height: auto !important;
          max-height: none !important; overflow: visible !important; resize: none !important;
        }
      `}</style> : null}
      <div className="ns-nbb-card-grid ns-nbb-summary-grid" style={{ display: focusedUnit ? "none" : "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 18 }}>
        {[
          [isFR ? "Pages prévues" : "Planned pages", totalPhysicalPages],
          [isFR ? "Nombre de mots" : "Word count", totalWords],
        ].map(([label, value]) => (
          <div key={label} className="ns-card ns-nbb-summary-card" style={{ padding: 16, borderRadius: 14 }}>
            <div className="ns-nbb-summary-label" style={{ fontSize: 12, fontWeight: 900, color: "#64748b" }}>{label}</div>
            <div className="ns-nbb-summary-value" style={{ marginTop: 5, fontSize: 24, fontWeight: 900 }}>{value}</div>
          </div>
        ))}
      </div>

      {focusedUnit && !visibleUnits.length ? (
        <div role="alert" className="ns-card" style={{padding:18}}>
          {isFR ? "Page ou double page introuvable." : "Selected page or spread not found."}
        </div>
      ) : units.length === 0 ? (
        <div className="ns-card ns-nbb-major-panel" style={{ padding: 22, borderRadius: 16, textAlign: "center" }}>
          <strong>{isFR ? "Le Plan du livre est vide" : "The Book Structure Is Empty"}</strong>
          <p className="ns-card-meta" style={{ margin: "8px auto 16px", maxWidth: 640 }}>
            {isFR
              ? "Ajoutez d’abord des pages ou doubles pages dans le Plan du livre."
              : "Add a Single Page or a Two-page Spread to the Book Structure first."}
          </p>

          <button
            className="ns-button ns-button-primary"
            type="button"
            onClick={onOpenBookMap}
          >
            {isFR
              ? "Ouvrir le Plan du livre et ajouter des pages"
              : "Open Book Structure"}
          </button>
        </div>
      ) : (
        <>
          {rangeCount > 1 && !focusedUnit ? (
            <section
              className="ns-card ns-nbb-major-panel"
              aria-label={
                isFR
                  ? "Navigation des pages et doubles pages du manuscrit"
                  : "Manuscript page and spread navigation"
              }
              style={{
                padding: 16,
                borderRadius: 16,
                marginBottom: 16,
                display: "grid",
                gap: 12,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent:
                    "space-between",
                  alignItems:
                    "center",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <strong>
                  {isFR
                    ? `Pages ${visiblePhysicalPageStart}–${visiblePhysicalPageEnd} sur ${totalPhysicalPages}`
                    : `Pages ${visiblePhysicalPageStart}–${visiblePhysicalPageEnd} of ${totalPhysicalPages}`}
                </strong>

                <span
                  className="ns-card-meta"
                >
                  {isFR
                    ? "Seule cette plage de pages physiques est affichée."
                    : "Only this physical page range is shown."}
                </span>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "auto minmax(220px, 1fr) auto",
                  gap: 10,
                  alignItems: "end",
                }}
              >
                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  disabled={
                    boundedRangeStart ===
                    0
                  }
                  onClick={() => {
                    setEditingId(
                      null
                    );

                    setDivisionUnitId(
                      null
                    );

                    setPendingDeleteId(
                      null
                    );

                    setRangeStart(
                      Math.max(
                        0,
                        boundedRangeStart -
                          MANUSCRIPT_RANGE_SIZE
                      )
                    );
                  }}
                >
                  {isFR
                    ? "Plage précédente"
                    : "Previous Range"}
                </button>

                <label
                  style={{
                    display: "grid",
                    gap: 6,
                    fontWeight: 800,
                  }}
                >
                  <span>
                    {isFR
                      ? "Aller à une page ou double page"
                      : "Jump to Page or Spread"}
                  </span>

                  <select
                    aria-label={
                      isFR
                        ? "Aller à une page ou double page"
                        : "Jump to Page or Spread"
                    }
                    value=""
                    onChange={(event) => {
                      const unitId =
                        cleanText(
                          event.target.value
                        );

                      const item =
                        unitNavigationItems.find(
                          (candidate) =>
                            cleanText(
                              candidate
                                .unit
                                ?.id
                            ) ===
                            unitId
                        );

                      if (!item) {
                        return;
                      }

                      const nextRangeStart =
                        Math.floor(
                          item.index /
                            MANUSCRIPT_RANGE_SIZE
                        ) *
                          MANUSCRIPT_RANGE_SIZE;

                      pendingScrollUnitIdRef.current =
                        unitId;

                      setPendingDeleteId(
                        null
                      );

                      setDivisionUnitId(
                        null
                      );

                      setRangeStart(
                        nextRangeStart
                      );

                      setEditingId(
                        unitId
                      );
                    }}
                    style={{
                      minHeight: 44,
                      padding:
                        "10px 12px",
                      borderRadius: 10,
                      border:
                        "1px solid #cbd5e1",
                      background: "#fff",
                      font: "inherit",
                    }}
                  >
                    <option value="">
                      {isFR
                        ? "Choisir une page ou double page"
                        : "Choose a page or spread"}
                    </option>

                    {unitNavigationItems.map(
                      (item) => {
                        const title =
                          cleanText(
                            item.unit
                              ?.workingTitle
                          );

                        const destination =
                          manuscriptUnitDestinationLabel(
                            item,
                            isFR
                          );

                        return (
                          <option
                            key={
                              item.unit
                                ?.id ||
                              item.index
                            }
                            value={
                              item.unit
                                ?.id ||
                              ""
                            }
                          >
                            {destination}
                            {title
                              ? ` · ${title}`
                              : ""}
                          </option>
                        );
                      }
                    )}
                  </select>
                </label>

                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  disabled={
                    boundedRangeStart >=
                    (
                      rangeCount - 1
                    ) *
                      MANUSCRIPT_RANGE_SIZE
                  }
                  onClick={() => {
                    setEditingId(
                      null
                    );

                    setDivisionUnitId(
                      null
                    );

                    setPendingDeleteId(
                      null
                    );

                    setRangeStart(
                      Math.min(
                        (
                          rangeCount - 1
                        ) *
                          MANUSCRIPT_RANGE_SIZE,

                        boundedRangeStart +
                          MANUSCRIPT_RANGE_SIZE
                      )
                    );
                  }}
                >
                  {isFR
                    ? "Plage suivante"
                    : "Next Range"}
                </button>
              </div>
            </section>
          ) : null}

          <div
            className="ns-nbb-major-grid"
            data-testid="manuscript-compact-unit-grid"
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(300px, 1fr))",
              gap: 12,
              alignItems: "start",
            }}
          >
            {visibleUnits.map((unit, visibleIndex) => {
              const index = focusedUnit
                ? unitNavigationItems.findIndex(item => cleanText(item.unit?.id) === cleanText(unit?.id))
                : boundedRangeStart + visibleIndex;

              const navigationItem =
                visibleNavigationItems[
                  visibleIndex
                ] ||
                unitNavigationItems[
                  index
                ] ||
                null;

              const previousNavigationItem =
                unitNavigationItems[
                  index - 1
                ] ||
                null;

              const nextNavigationItem =
                unitNavigationItems[
                  index + 1
                ] ||
                null;

            const entry = entryFor(unit.id);
            const editing = editingId === unit.id;
            const words =
              countWordsFromManuscriptEntry(
                entry
              );

            const textRequired =
              unitRequiresText(
                unit
              );

            const divisionNeeded =
              textRequired &&
              words >
              MANUSCRIPT_PAGE_MAXIMUM_WORDS;

            const isComplete =
              entry.status === "complete";

            return (
              <article
                id={`manuscript-unit-${encodeURIComponent(
                  String(unit.id || "")
                )}`}
                key={unit.id}
                className="ns-card ns-nbb-major-panel"
                data-testid="manuscript-unit-row"
                data-novel-writer-v1={novelWritingStyle && !(manuscriptFullPageSingleLayoutApplies(unit) ||
                  manuscriptFullPageSpreadLayoutApplies(unit) ||
                  manuscriptSharedIllustrationPageLayoutApplies(unit)) ? "true" : "false"}
                data-active={
                  editing
                    ? "true"
                    : "false"
                }
                style={{
                  padding:
                    editing
                      ? 20
                      : 14,
                  borderRadius: 18,
                  border:
                    editing
                      ? "2px solid rgba(11,112,61,0.54)"
                      : "1px solid rgba(234,88,12,0.20)",
                  gridColumn:
                    editing
                      ? "1 / -1"
                      : "auto",
                  boxShadow:
                    editing
                      ? "0 16px 36px rgba(15,23,42,0.10)"
                      : "0 5px 14px rgba(15,23,42,0.04)",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <div>
                    <strong className="ns-nbb-unit-title">
                      {manuscriptUnitWriterPageLabel(
                        navigationItem,
                        isFR
                      )}.{" "}
                      {unit.workingTitle ||
                        unitLabel(unit)}
                    </strong>
                    <p className="ns-card-meta" style={{ margin: "6px 0 0" }}>
                      {unitLabel(unit)}
                    </p>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      gap: 9,
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      aria-label={
                        isFR
                          ? "État du manuscrit"
                          : "Manuscript status"
                      }
                      style={{
                        display: "inline-flex",
                        padding: "6px 10px",
                        borderRadius: 999,
                        fontSize: 12,
                        fontWeight: 900,
                        background:
                          !textRequired ||
                          isComplete
                            ? "#dcfce7"
                            : words
                              ? "#fef3c7"
                              : "#e2e8f0",
                        color:
                          !textRequired ||
                          isComplete
                            ? "#166534"
                            : words
                              ? "#92400e"
                              : "#475569",
                      }}
                    >
                      {!textRequired
                        ? (
                            isFR
                              ? "Terminé"
                              : "Complete"
                          )
                        : writingStatusLabel(
                            entry.status
                          )}
                    </span>

                    {textRequired ? (
                      <span
                        aria-label={
                          isFR
                            ? "Nombre de mots"
                            : "Word count"
                        }
                        style={{
                          display: "inline-flex",
                          padding: "6px 10px",
                          borderRadius: 999,
                          fontSize: 12,
                          fontWeight: 850,
                          background:
                            "#f1f5f9",
                          color:
                            "#334155",
                        }}
                      >
                        {isFR
                          ? `${words} mot(s)`
                          : `${words} word(s)`}
                      </span>
                    ) : null}

                    {!textRequired ? (
                      <span
                        aria-label={
                          isFR
                            ? "Texte non requis"
                            : "Text not required"
                        }
                        style={{
                          display: "inline-flex",
                          padding: "6px 10px",
                          borderRadius: 999,
                          fontSize: 12,
                          fontWeight: 900,
                          background: "#ecfdf5",
                          color: "#166534",
                        }}
                      >
                        {isFR
                          ? "Aucun texte requis"
                          : "No text required"}
                      </span>
                    ) : null}

                    {divisionNeeded &&
                    !editing ? (
                      <span
                        style={{
                          display: "inline-flex",
                          padding: "6px 10px",
                          borderRadius: 999,
                          fontSize: 12,
                          fontWeight: 900,
                          background:
                            "#ffedd5",
                          color:
                            "#9a3412",
                        }}
                      >
                        {isFR
                          ? "Division nécessaire"
                          : "Needs division"}
                      </span>
                    ) : null}

                    {textRequired &&
                    words > 0 &&
                    !isComplete &&
                    (
                      editing ||
                      units.length === 1
                    ) ? (
                      <button
                        className="ns-button ns-button-primary"
                        type="button"
                        onClick={() =>
                          updateEntry(
                            unit.id,
                            "status",
                            "complete"
                          )
                        }
                      >
                        {isFR
                          ? "Marquer le manuscrit comme terminé"
                          : "Mark Manuscript Complete"}
                      </button>
                    ) : null}

                    {divisionNeeded &&
                    (
                      editing ||
                      units.length === 1
                    ) ? (
                      divisionUnitId ===
                      unit.id ? (
                        <span
                          className="ns-button ns-button-outline"
                          role="status"
                          aria-label={
                            isFR
                              ? "Révision de la division ouverte"
                              : "Division Review Open"
                          }
                          style={{
                            cursor:
                              "default",
                          }}
                        >
                          {isFR
                            ? "Révision de la division ouverte"
                            : "Division Review Open"}
                        </span>
                      ) : (
                        <button
                          className="ns-button ns-button-primary"
                          type="button"
                          onClick={() => {
                            setEditingId(
                              null
                            );

                            setDivisionOutcome(
                              null
                            );

                            setDivisionUnitId(
                              unit.id
                            );
                          }}
                        >
                          {isFR
                            ? "Réviser les pages proposées"
                            : "Review Proposed Pages"}
                        </button>
                      )
                    ) : null}

                    <button
                      className="ns-button ns-button-outline"
                      type="button"
                      onClick={() => {
                        if (editing) {
                          if (focusedUnit) { onReturn?.(); return; }
                          setEditingId(
                            null
                          );
                          return;
                        }

                        openManuscriptUnit(
                          unit.id
                        );
                      }}
                    >
                      {editing
                        ? (
                            !textRequired
                              ? (
                                  isFR
                                    ? "Fermer les détails"
                                    : "Close Details"
                                )
                              : (
                                  isFR
                                    ? "Fermer l’éditeur"
                                    : "Close Editor"
                                )
                          )
                        : !textRequired
                          ? (
                              isFR
                                ? "Voir les détails"
                                : "View Details"
                            )
                          : words
                            ? (
                                isFR
                                  ? "Continuer la rédaction"
                                  : "Continue Writing"
                              )
                            : (
                                isFR
                                  ? "Commencer la rédaction"
                                  : "Start Writing"
                              )}
                    </button>
                  </div>
                </div>

                {divisionNeeded &&
                !editing ? (
                  <div
                    role="alert"
                    style={{
                      marginTop: 12,
                      padding: "9px 11px",
                      borderRadius: 10,
                      background:
                        "#fff7ed",
                      border:
                        "1px solid rgba(234,88,12,0.24)",
                      color: "#9a3412",
                      fontSize: 13,
                      lineHeight: 1.45,
                    }}
                  >
                    {isFR
                      ? `${words} mots sont actuellement regroupés dans une seule unité de manuscrit. Révisez leur répartition entre les pages physiques.`
                      : `${words} words are currently grouped in one manuscript unit. Review how the text should be divided between physical pages.`}
                  </div>
                ) : null}

                {divisionNeeded &&
                editing ? (
                  <div
                    role="alert"
                    style={{
                      marginTop: 16,
                      padding: 13,
                      borderRadius: 12,
                      background:
                        "#fff7ed",
                      border:
                        "1px solid rgba(234,88,12,0.30)",
                      color: "#9a3412",
                      lineHeight: 1.55,
                    }}
                  >
                    {isFR
                      ? `${words} mots sont actuellement regroupés dans une seule unité de manuscrit. Révisez leur répartition entre les pages physiques.`
                      : `${words} words are currently grouped in one manuscript unit. Review how the text should be divided between physical pages.`}
                  </div>
                ) : null}

                {editing ? (
                  <div
                    className="ns-nbb-manuscript-context-grid"
                    data-testid="manuscript-unit-context"
                    style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginTop: 16 }}
                  >
                  {[
                    [isFR ? "Ce qui se passe ici" : "What happens here", unit.purpose],
                    [isFR ? "Quantité de texte" : "Expected text amount", unit.textAmount],
                    [isFR ? "Illustration prévue" : "Illustration plan", illustrationLabel(unit.illustrationPlan)],
                    [isFR ? "Disposition" : "Layout intention", unit.layoutIntention],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      className="ns-nbb-inner-panel ns-nbb-manuscript-context-item"
                      style={{ padding: 12, borderRadius: 12, background: "#f8fafc" }}
                    >
                      <div className="ns-nbb-detail-label" style={{ fontSize: 12, fontWeight: 900, color: "#64748b" }}>{label}</div>
                      <div className="ns-nbb-detail-value" style={{ marginTop: 5, fontWeight: 700 }}>
                        {String(value || "").trim() || (isFR ? "Non renseigné" : "Not provided")}
                      </div>
                    </div>
                  ))}
                  </div>
                ) : null}

                {editing &&
                manuscriptFullPageSingleLayoutApplies(
                  unit
                ) ? (
                  <ChildrensBookManuscriptFullPageSingleLayout
                    lang={lang}
                    unit={unit}
                    pageNumber={
                      navigationItem
                        ?.pageStart
                    }
                    childrensBookAssets={
                      childrensBookAssets
                    }
                    onLoadChildrensBookAssetFile={
                      onLoadChildrensBookAssetFile
                    }
                    plannedTextRequired={
                      textRequired
                    }
                    attachedWordCount={
                      words
                    }
                    previousLabel={
                      manuscriptAdjacentNavigationLabel(
                        previousNavigationItem,
                        -1,
                        isFR
                      )
                    }
                    nextLabel={
                      manuscriptAdjacentNavigationLabel(
                        nextNavigationItem,
                        1,
                        isFR
                      )
                    }
                    previousDisabled={
                      index <= 0
                    }
                    nextDisabled={
                      index >=
                      units.length - 1
                    }
                    onPrevious={() =>
                      moveToAdjacentManuscriptUnit(
                        -1
                      )
                    }
                    onNext={() =>
                      moveToAdjacentManuscriptUnit(
                        1
                      )
                    }
                  />
                ) : null}

                {editing &&
                !textRequired &&
                !manuscriptFullPageSingleLayoutApplies(
                  unit
                ) ? (
                  <div
                    className="ns-nbb-editing-panel ns-nbb-manuscript-no-text-shell"
                    data-testid="manuscript-no-text-panel"
                    style={{
                      display: "grid",
                      gap: 16,
                      marginTop: 18,
                    }}
                  >
                    <div
                      className="ns-nbb-manuscript-unit-nav"
                      style={{
                        display: "flex",
                        justifyContent:
                          "space-between",
                        alignItems:
                          "center",
                        gap: 12,
                        flexWrap:
                          "wrap",
                        paddingBottom:
                          12,
                        borderBottom:
                          "1px solid #e2e8f0",
                      }}
                    >
                      <button
                        className="ns-button ns-button-outline"
                        type="button"
                        disabled={
                          index <= 0
                        }
                        onClick={() =>
                          moveToAdjacentManuscriptUnit(
                            -1
                          )
                        }
                      >
                        {manuscriptAdjacentNavigationLabel(
                          previousNavigationItem,
                          -1,
                          isFR
                        )}
                      </button>

                      <strong>
                        {isFR
                          ? `Affichage — ${manuscriptUnitWriterPageLabel(
                              navigationItem,
                              isFR
                            )}`
                          : `Viewing ${manuscriptUnitWriterPageLabel(
                              navigationItem,
                              isFR
                            )}`}
                      </strong>

                      <button
                        className="ns-button ns-button-outline"
                        type="button"
                        disabled={
                          index >=
                          units.length - 1
                        }
                        onClick={() =>
                          moveToAdjacentManuscriptUnit(
                            1
                          )
                        }
                      >
                        {manuscriptAdjacentNavigationLabel(
                          nextNavigationItem,
                          1,
                          isFR
                        )}
                      </button>
                    </div>

                    <div
                      role="status"
                      style={{
                        padding: 16,
                        borderRadius: 14,
                        border:
                          "1px solid rgba(22,101,52,0.24)",
                        background: "#f0fdf4",
                        color: "#166534",
                      }}
                    >
                      <strong>
                        {isFR
                          ? "Aucun texte requis"
                          : "No text required"}
                      </strong>

                      <p
                        className="ns-card-meta"
                        style={{
                          margin:
                            "8px 0 0",
                          lineHeight:
                            1.55,
                          color:
                            "inherit",
                        }}
                      >
                        {isFR
                          ? "Cette partie du livre est intentionnellement définie sur « Aucun texte » dans la Structure du livre."
                          : "This part of the book is intentionally set to “No text” in Book Structure."}
                      </p>

                      <p
                        className="ns-card-meta"
                        style={{
                          margin:
                            "7px 0 0",
                          lineHeight:
                            1.55,
                          color:
                            "inherit",
                        }}
                      >
                        {isFR
                          ? "Pour ajouter du texte de manuscrit ici, modifiez d’abord son besoin de texte dans la Structure du livre."
                          : "To add manuscript text here, first change its text requirement in Book Structure."}
                      </p>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        justifyContent:
                          "flex-end",
                      }}
                    >
                      <button
                        className="ns-button ns-button-primary"
                        type="button"
                        onClick={() =>
                          setEditingId(
                            null
                          )
                        }
                      >
                        {isFR
                          ? "Fermer et revenir"
                          : "Close and Return"}
                      </button>
                    </div>
                  </div>
                ) : null}

                {editing &&
                textRequired &&
                !manuscriptFullPageSingleLayoutApplies(
                  unit
                ) ? (
                  <div
                    className="ns-nbb-editing-panel ns-nbb-manuscript-active-shell"
                    data-testid="manuscript-active-editor"
                    style={{
                      display: "grid",
                      gap: 16,
                      marginTop: 18,
                    }}
                  >
                    <div
                      className="ns-nbb-manuscript-unit-nav"
                      style={{
                        display: "flex",
                        justifyContent:
                          "space-between",
                        alignItems:
                          "center",
                        gap: 12,
                        flexWrap:
                          "wrap",
                        paddingBottom:
                          12,
                        borderBottom:
                          "1px solid #e2e8f0",
                      }}
                    >
                      <button
                        className="ns-button ns-button-outline"
                        type="button"
                        disabled={
                          index <= 0
                        }
                        onClick={() =>
                          moveToAdjacentManuscriptUnit(
                            -1
                          )
                        }
                      >
                        {manuscriptAdjacentNavigationLabel(
                          previousNavigationItem,
                          -1,
                          isFR
                        )}
                      </button>

                      <strong>
                        {isFR
                          ? `Modification — ${manuscriptUnitWritingDestinationLabel(
                              navigationItem,
                              isFR
                            )}`
                          : `Editing ${manuscriptUnitWritingDestinationLabel(
                              navigationItem,
                              isFR
                            )}`}
                      </strong>

                      <button
                        className="ns-button ns-button-outline"
                        type="button"
                        disabled={
                          index >=
                          units.length - 1
                        }
                        onClick={() =>
                          moveToAdjacentManuscriptUnit(
                            1
                          )
                        }
                      >
                        {manuscriptAdjacentNavigationLabel(
                          nextNavigationItem,
                          1,
                          isFR
                        )}
                      </button>
                    </div>
                    <ChildrensBookManuscriptFullPageSpreadLayout
                      lang={lang}
                      unit={unit}
                      pageStart={
                        navigationItem
                          ?.pageStart
                      }
                      pageEnd={
                        navigationItem
                          ?.pageEnd
                      }
                      childrensBookAssets={
                        childrensBookAssets
                      }
                      onLoadChildrensBookAssetFile={
                        onLoadChildrensBookAssetFile
                      }
                    >
                      <ChildrensBookManuscriptSharedIllustrationPageLayout
                        lang={lang}
                        unit={unit}
                        pageNumber={
                          navigationItem
                            ?.pageStart
                        }
                        childrensBookAssets={
                          childrensBookAssets
                        }
                        onLoadChildrensBookAssetFile={
                          onLoadChildrensBookAssetFile
                        }
                        editorOwnsIllustration={
                          true
                        }
                      >
                      <div
                        className="ns-nbb-manuscript-writing-section"
                        style={{
                          display:
                            "grid",
                          gap:
                            7,
                          fontWeight:
                            800,
                          ...(manuscriptSharedIllustrationPageLayoutApplies(
                            unit
                          )
                            ? {
                                padding:
                                  0,
                                border:
                                  "none",
                                borderRadius:
                                  0,
                                background:
                                  "transparent",
                                boxShadow:
                                  "none",
                              }
                            : {}),
                        }}
                      >
                      <span className="ns-nbb-manuscript-writing-heading">
                        {isFR ? "Texte du manuscrit" : "Manuscript text"}
                      </span>
                      <span className="ns-card-meta ns-nbb-manuscript-writing-intro">
                        {isFR ? "Rédigez les mots que le jeune lecteur verra ou entendra." : "Write the words the young reader will see or hear."}
                      </span>

                      {liveFlowAdvisory ? (
                        <div
                          data-testid="manuscript-live-flow-advisory"
                          data-flow-kind={
                            liveFlowAdvisory.kind
                          }
                          style={{
                            padding:
                              "9px 11px",
                            borderRadius:
                              10,
                            border:
                              "1px solid #d6b65e",
                            background:
                              "#fff9e8",
                            color:
                              "#14532d",
                            fontSize:
                              13,
                            fontWeight:
                              800,
                            lineHeight:
                              1.45,
                          }}
                        >
                          {
                            liveFlowAdvisory
                              .text
                          }
                        </div>
                      ) : null}

                      <ChildrensBookManuscriptEditor
                        key={unit.id}
                        {...childrensBookWriterPresentationV1({enabled: novelWritingStyle, readOnly, pageConstrained: (manuscriptFullPageSingleLayoutApplies(unit) ||
                  manuscriptFullPageSpreadLayoutApplies(unit) ||
                  manuscriptSharedIllustrationPageLayoutApplies(unit))})}
                        lang={lang}
                        entry={entry}
                        selectionRestore={
                          cleanText(
                            manuscriptSelectionRestore
                              ?.unitId
                          ) ===
                          cleanText(
                            unit.id
                          )
                            ? manuscriptSelectionRestore
                            : null
                        }
                        pageCanvasTrail={
                          manuscriptSharedIllustrationPageLayoutApplies(
                            unit
                          )
                            ? (
                                <ChildrensBookManuscriptIllustrationContext
                                  lang={lang}
                                  unit={unit}
                                  childrensBookAssets={
                                    childrensBookAssets
                                  }
                                  onLoadChildrensBookAssetFile={
                                    onLoadChildrensBookAssetFile
                                  }
                                  presentation={
                                    manuscriptSharedIllustrationPageProfile(
                                      unit
                                    )
                                      ?.presentation
                                  }
                                />
                              )
                            : null
                        }
                        pageCanvasLabel={
                          manuscriptSharedIllustrationPageLayoutApplies(
                            unit
                          )
                            ? `${
                                manuscriptUnitWritingDestinationLabel(
                                  navigationItem,
                                  isFR
                                )
                              } — ${
                                isFR
                                  ? "illustration et texte"
                                  : "illustration and manuscript"
                              }`
                            : ""
                        }
                        onChange={(fields) =>
                          handleManuscriptEditorChange(
                            unit.id,
                            fields
                          )
                        }
                      />
                      </div>
                      </ChildrensBookManuscriptSharedIllustrationPageLayout>
                    </ChildrensBookManuscriptFullPageSpreadLayout>

                    {/* Writer first: ordinary illustration context follows manuscript editing. */}
                    {editing &&
                    !(
                      textRequired &&
                      manuscriptFullPageSpreadLayoutApplies(
                        unit
                      )
                    ) &&
                    !manuscriptFullPageSingleLayoutApplies(
                      unit
                    ) &&
                    !(
                      textRequired &&
                      manuscriptSharedIllustrationPageLayoutApplies(
                        unit
                      )
                    ) &&
                    cleanText(
                      unit?.illustrationPlan
                    ).toLowerCase() !==
                      "none" &&
                    Boolean(
                      cleanText(
                        unit?.illustrationPlan
                      )
                    ) ? (
                      <ChildrensBookManuscriptIllustrationContext
                        lang={lang}
                        unit={unit}
                        childrensBookAssets={
                          childrensBookAssets
                        }
                        onLoadChildrensBookAssetFile={
                          onLoadChildrensBookAssetFile
                        }
                      />
                    ) : null}

                    <label
                      className="ns-nbb-manuscript-support-field ns-nbb-manuscript-author-note"
                      style={{ display: "grid", gap: 7, fontWeight: 800 }}
                    >
                      <span>{isFR ? "Note de l’auteur" : "Author note"}</span>
                      <textarea
                        rows={4}
                        readOnly={readOnly}
                        value={entry.authorNote}
                        placeholder={isFR ? "Ton, rythme, continuité..." : "Tone, rhythm, continuity..."}
                        onChange={(event) => updateEntry(unit.id, "authorNote", event.target.value)}
                        style={{ width: "100%", boxSizing: "border-box", padding: 12, borderRadius: 10, border: "1px solid #cbd5e1", font: "inherit", resize: "vertical" }}
                      />
                    </label>

                    <label
                      className="ns-nbb-manuscript-support-field ns-nbb-manuscript-status-field"
                      style={{ display: "grid", gap: 7, fontWeight: 800 }}
                    >
                      <span>{isFR ? "État de rédaction" : "Writing status"}</span>
                      <select
                        disabled={readOnly}
                        value={entry.status}
                        onChange={(event) => updateEntry(unit.id, "status", event.target.value)}
                        style={{ minHeight: 44, padding: "10px 12px", borderRadius: 10, border: "1px solid #cbd5e1", background: "#fff", font: "inherit" }}
                      >
                        {statusOptions.map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>

                      <span
                        className="ns-card-meta"
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          lineHeight: 1.45,
                        }}
                      >
                        {isFR
                          ? "Choisissez « Terminé » uniquement lorsque le texte de cette unité est achevé."
                          : "Choose “Complete” only when the text for this unit is finished."}
                      </span>
                    </label>

                    <div
                      className="ns-nbb-manuscript-editor-footer"
                      data-testid="manuscript-editor-footer"
                      style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}
                    >
                      <strong>{isFR ? `${words} mot(s)` : `${words} word(s)`}</strong>

                      <div
                        style={{
                          display: "flex",
                          gap: 10,
                          flexWrap: "wrap",
                        }}
                      >
                        {!readOnly && entries.some(
                          (item) => item.unitId === unit.id
                        ) ? (
                          <button
                            className="ns-button ns-button-outline"
                            type="button"
                            onClick={() => requestDeleteEntry(unit.id)}
                            style={{
                              borderColor: "#dc2626",
                              color: "#b91c1c",
                            }}
                          >
                            {isFR
                              ? "Supprimer le brouillon"
                              : "Delete Draft"}
                          </button>
                        ) : null}

                        <button
                          className="ns-button ns-button-primary"
                          type="button"
                          onClick={() => focusedUnit ? onReturn?.() : setEditingId(null)}
                        >
                          {isFR
                            ? "Fermer et revenir"
                            : "Close and Return"}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}
              </article>
            );
          })}
          </div>
        </>
      )}

      {divisionOutcome ? (
        <div
          className="ns-card ns-nbb-major-panel"
          role="status"
          data-testid="manuscript-division-outcome"
          style={{
            padding: 16,
            marginTop: 18,
            borderRadius: 14,
            border:
              "1px solid rgba(22,101,52,0.28)",
            background: "#f0fdf4",
            color: "#166534",
          }}
        >
          <strong>
            {isFR
              ? "Division appliquée et enregistrée."
              : "Division applied and saved."}
          </strong>

          <p
            className="ns-card-meta"
            style={{
              margin: "7px 0 0",
              lineHeight: 1.55,
              color: "inherit",
            }}
          >
            {isFR
              ? `L’unité d’origine comprend maintenant ${divisionOutcome.segmentCount} Pages simples. ${divisionOutcome.sourceContentMode === "structured" ? "La mise en forme du manuscrit a été préservée." : "Le texte du manuscrit a été préservé."}`
              : `The original unit is now ${divisionOutcome.segmentCount} Single Pages. ${divisionOutcome.sourceContentMode === "structured" ? "Manuscript formatting was preserved." : "Manuscript wording was preserved."}`}
          </p>
        </div>
      ) : null}

      {divisionUnitId ? (
        <ChildrensBookManuscriptUnitDivisionReview
          lang={lang}
          projectPlan={
            divisionProjectPlan
          }
          sourceUnitId={
            divisionUnitId
          }
          childrensBookAssets={
            childrensBookAssets
          }
          saving={
            manuscriptUnitDivisionSaving
          }
          onApply={
            onApplyManuscriptUnitDivision
          }
          onClose={
            closeDivisionReview
          }
        />
      ) : null}

      {pendingDeleteId ? (
        <form
          className="ns-card ns-nbb-major-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="manuscript-delete-title"
          aria-describedby="manuscript-delete-description"
          onSubmit={confirmDeleteEntry}
          style={{
            padding: 20,
            marginTop: 18,
            borderRadius: 16,
            border:
              "1px solid rgba(220,38,38,0.28)",
            background: "#fff7f7",
          }}
        >
          <h3
            id="manuscript-delete-title"
            style={{ margin: "0 0 8px" }}
          >
            {isFR
              ? "Supprimer ce brouillon du manuscrit ?"
              : "Delete This Manuscript Draft?"}
          </h3>

          <p
            id="manuscript-delete-description"
            className="ns-card-meta"
            style={{
              margin: "0 0 16px",
              lineHeight: 1.6,
            }}
          >
            {isFR
              ? "Le texte du manuscrit, la note de l’auteur et l’état de rédaction de cette page ou double page seront supprimés. L’unité restera dans le Plan du livre."
              : "The manuscript text, author note, and writing status for this page or spread will be removed. The page or spread will remain in the Book Structure."}
          </p>

          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <button
              className="ns-button ns-button-outline"
              type="button"
              onClick={cancelDeleteEntry}
            >
              {isFR ? "Annuler" : "Cancel"}
            </button>

            <button
              className="ns-button ns-button-primary"
              type="submit"
              style={{
                background: "#b91c1c",
                borderColor: "#b91c1c",
              }}
            >
              {isFR
                ? "Supprimer le brouillon"
                : "Delete Draft"}
            </button>
          </div>
        </form>
      ) : null}

      <div className="ns-card" style={{ padding: 16, marginTop: 18, borderRadius: 16, background: "#fff7ed", border: "1px solid rgba(234,88,12,0.25)" }}>
        <strong>
          {isFR
            ? "Enregistrement avec le Blueprint"
            : "Saved with the Blueprint"}
        </strong>
        <p className="ns-card-meta" style={{ margin: "7px 0 0", lineHeight: 1.55 }}>
          {isFR
            ? "Le texte du manuscrit fait partie des données du Blueprint et est inclus lorsque le projet est enregistré. Les modifications non enregistrées restent dans cette session jusqu’au prochain enregistrement."
            : "Manuscript text forms part of the Blueprint data and is included when the project is saved. Unsaved changes remain in this session until the next save."}
        </p>
      </div>

      <div style={{ marginTop: 18 }}>
        <button className="ns-button ns-button-outline" type="button" onClick={onReturn}>
          {focusedUnit ? (isFR ? "← Retour au chapitre" : "← Back to Chapter") : returnToWholeBookReview
            ? (
                isFR
                  ? "Retour à la révision complète du livre"
                  : "Return to Whole Book Review"
              )
            : (
                isFR
                  ? "Retour au NextScenes Book Blueprint"
                  : "Return to NextScenes Book Blueprint"
              )}
        </button>
      </div>
    </section>
  );
}
